import { useState, useEffect, useRef, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import API from "../services/api";

export function useDialer() {
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timerRef = useRef(null);
  const callStartRef = useRef(null);
  const identityRef = useRef("");
  const ringStartRef = useRef(null);
  const holdStartRef = useRef(null);
  const holdDurationRef = useRef(0);
  const callTypeRef = useRef("direct");
  const dialParamsRef = useRef({ to: "", from: "" });

  const [status, setStatus] = useState("idle");
  const [isReady, setIsReady] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState(null);
  const [identity, setIdentity] = useState("");
  const [queueCalls, setQueueCalls] = useState([]);
  const [pendingQueueCall, setPendingQueueCall] = useState(null);
  const queuePollRef = useRef(null);
  const [callStartTime, setCallStartTime] = useState(null);

  const startTimer = useCallback(() => {
    const now = new Date();
    setCallDuration(0);
    setCallStartTime(now);
    callStartRef.current = now;
    timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    let dev;
    (async () => {
      try {
        const { data } = await API.get("/twilio/token");
        setIdentity(data.identity);
        identityRef.current = data.identity;

        dev = new Device(data.token, {
          logLevel: 1,
          codecPreferences: ["opus", "pcmu"],
          enableIceRestart: true,
        });

        dev.on("registered", () => { setIsReady(true); setStatus("idle"); });
        dev.on("unregistered", () => setIsReady(false));
        dev.on("error", async (err) => {
          // Auto-refresh on token expiry
          if (err.code === 20104 || err.message?.includes("AccessTokenExpired") || err.message?.includes("20104")) {
            setError(null);
            setStatus("idle");
            try {
              const { data } = await API.get("/twilio/token");
              dev.updateToken(data.token);
              await dev.register();
              setIsReady(true);
              setStatus("idle");
            } catch (refreshErr) {
              setError("Connection lost. Reconnecting...");
              setStatus("error");
              // Retry after 5 seconds
              setTimeout(async () => {
                try {
                  const { data } = await API.get("/twilio/token");
                  dev.updateToken(data.token);
                  await dev.register();
                  setIsReady(true);
                  setStatus("idle");
                  setError(null);
                } catch {}
              }, 5000);
            }
          } else {
            setError(err.message);
            setStatus("error");
          }
        });
        dev.on("incoming", async (call) => {
      // Try to get real caller number for ElevenLabs transfers
      try {
        const { data } = await API.get(`/twilio/caller-lookup?callSid=${call.parameters.CallSid}`);
        console.log("Caller lookup result:", data);
        if (data.from) {
          call.parameters.From = data.from;
          console.log("Updated From to:", data.from);
        }
      } catch (err) { console.error("Caller lookup failed:", err); }
          ringStartRef.current = new Date();
          callTypeRef.current = "direct";
          setIncomingCall(call);
          call.on("cancel", () => {
            setIncomingCall(null);
            const params = call.parameters || {};
            API.post("/calls", {
              call_sid: params.CallSid || ("missed-" + Date.now()),
              direction: "inbound",
              from_number: params.From || "",
              to_number: params.To || "",
              status: "no-answer",
              duration: 0,
              started_at: new Date(),
              agent_identity: identityRef.current || "",
            }).catch(() => {});
          });
        });

        await dev.register();
        deviceRef.current = dev;
      } catch (err) {
        setError("Failed to initialize: " + (err.response?.data?.error || err.message));
        setStatus("error");
      }
    })();
    // Proactive token refresh every 50 minutes (tokens expire in 60min)
    const tokenRefreshInterval = setInterval(async () => {
      if (!dev) return;
      try {
        const { data } = await API.get("/twilio/token");
        dev.updateToken(data.token);
      } catch {}
    }, 50 * 60 * 1000);

    // Poll queue every 5 seconds
    queuePollRef.current = setInterval(async () => {
      try {
        const { data } = await API.get("/twilio/queue-calls");
        setQueueCalls(data);
        // If not in call and there are waiting calls, show notification for first one
        if (data.length > 0 && !callRef.current) {
          setPendingQueueCall(prev => prev?.call_sid === data[0].call_sid ? prev : data[0]);
        } else if (data.length === 0) {
          setPendingQueueCall(null);
        }
      } catch {}
    }, 5000);

    return () => { dev?.destroy(); clearInterval(timerRef.current); clearInterval(tokenRefreshInterval); clearInterval(queuePollRef.current); };
  }, []);

  const bindCallEvents = useCallback((call) => {
    call.on("accept", () => {
      setStatus("in-call");
      startTimer();
      setIncomingCall(null);
      holdDurationRef.current = 0;
      callStartRef.current = new Date();
    });
    call.on("disconnect", () => {
      // Save call to history
      const duration = Math.floor((new Date() - (callStartRef.current || new Date())) / 1000);
      const params = call.parameters || {};
      const isInbound = call.direction === "INCOMING" || call.direction === "inbound";
      console.log("Call disconnect - direction:", call.direction, "isInbound:", isInbound, "params:", JSON.stringify(params), "dialParams:", JSON.stringify(dialParamsRef.current));
      const ringDuration = ringStartRef.current
        ? Math.floor((callStartRef.current - ringStartRef.current) / 1000)
        : 0;
      API.post("/calls", {
        call_sid: params.CallSid || ("local-" + Date.now()),
        direction: isInbound ? "inbound" : "outbound",
        from_number: isInbound ? (params.From || "") : dialParamsRef.current.from,
        to_number: isInbound ? (params.To || "") : dialParamsRef.current.to,
        status: "completed",
        duration: duration,
        hold_duration: holdDurationRef.current || 0,
        ring_duration: ringDuration > 0 ? ringDuration : 0,
        call_type: callTypeRef.current || "direct",
        started_at: callStartRef.current || new Date(),
        agent_identity: identityRef.current || "",
        answered_at: callStartRef.current || new Date(),
      }).catch((err) => { console.error("Failed to save call:", err); });
      ringStartRef.current = null;
      holdDurationRef.current = 0;
      callTypeRef.current = "direct";
      setStatus("idle");
      setActiveCall(null);
      setIsMuted(false);
      setIsOnHold(false);
      callRef.current = null;
      stopTimer();
      setCallDuration(0);
    });
    call.on("cancel", () => {
      setStatus("idle");
      setActiveCall(null);
      callRef.current = null;
      stopTimer();
      setCallDuration(0);
    });
    call.on("error", (err) => { setError(err.message); setStatus("error"); });
    callRef.current = call;
    setActiveCall(call);
  }, [startTimer, stopTimer]);

  const dequeueCall = useCallback(async (callSid, queueSid) => {
    callTypeRef.current = "queue";
    try {
      await API.post("/twilio/dequeue", { call_sid: callSid, queue_sid: queueSid });
      setPendingQueueCall(null);
      setQueueCalls(q => q.filter(c => c.call_sid !== callSid));
    } catch (err) { setError(err.response?.data?.error || "Failed to dequeue"); }
  }, []);

  const declineQueueCall = useCallback(async (callSid, queueSid) => {
    try {
      await API.post("/twilio/queue-decline", { call_sid: callSid, queue_sid: queueSid });
      setPendingQueueCall(null);
      setQueueCalls(q => q.filter(c => c.call_sid !== callSid));
    } catch (err) { setError(err.response?.data?.error || "Failed to decline"); }
  }, []);

  const dial = useCallback(async (number, callerId) => {
    dialParamsRef.current = { to: number, from: callerId || "" };
    if (!deviceRef.current || !number) return;
    setStatus("connecting");
    setError(null);
    try {
      const call = await deviceRef.current.connect({
        params: { To: number, CallerId: callerId || "" },
      });
      setStatus("ringing");
      bindCallEvents(call);
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  }, [bindCallEvents]);

  const hangup = useCallback(() => {
    callRef.current?.disconnect();
    deviceRef.current?.disconnectAll();
  }, []);

  const accept = useCallback(() => {
    if (!incomingCall) return;
    const call = incomingCall;
    setIncomingCall(null);
    bindCallEvents(call);
    call.accept();
    setStatus("in-call");
    startTimer();
  }, [incomingCall, bindCallEvents, startTimer]);

  const reject = useCallback(() => {
    incomingCall?.reject();
    setIncomingCall(null);
  }, [incomingCall]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !isMuted;
    call.mute(next);
    setIsMuted(next);
  }, [isMuted]);

  const toggleHold = useCallback(() => {
    if (!isOnHold) {
      holdStartRef.current = new Date();
    } else if (holdStartRef.current) {
      holdDurationRef.current += Math.floor((new Date() - holdStartRef.current) / 1000);
      holdStartRef.current = null;
    }
    const call = callRef.current;
    if (!call) return;
    const next = !isOnHold;
    call.mute(next);
    setIsOnHold(next);
    if (!next) setIsMuted(false);
  }, [isOnHold]);

  const sendDTMF = useCallback((digit) => {
    callRef.current?.sendDigits(digit);
  }, []);

  const formatDuration = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  const isInCall = ["in-call", "connecting", "ringing"].includes(status);

  return {
    status, isReady, isInCall, identity,
    activeCall, incomingCall,
    isMuted, isOnHold,
    callDuration, formattedDuration: formatDuration(callDuration),
    callStartTime, error,
    dial, hangup, accept, reject, dequeueCall, declineQueueCall, queueCalls, pendingQueueCall, setPendingQueueCall,
    toggleMute, toggleHold, sendDTMF,
  };
}
